import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "eggplant-video-favorites";
const SYNC_EVENT = "eggplant-favorites-updated";

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // storage may be unavailable in private mode, state still works in memory
  }
  window.dispatchEvent(new CustomEvent<string[]>(SYNC_EVENT, { detail: ids }));
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const sync = (event: Event) => {
      if (event instanceof CustomEvent && Array.isArray(event.detail)) {
        setFavorites(event.detail as string[]);
      } else {
        setFavorites(readFavorites());
      }
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) setFavorites(readFavorites());
    };

    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const toggle = useCallback((videoId: string) => {
    const current = readFavorites();
    const next = current.includes(videoId)
      ? current.filter((id) => id !== videoId)
      : [videoId, ...current];
    writeFavorites(next);
    setFavorites(next);
    return next.includes(videoId);
  }, []);

  const remove = useCallback((videoId: string) => {
    const next = readFavorites().filter((id) => id !== videoId);
    writeFavorites(next);
    setFavorites(next);
  }, []);

  const clear = useCallback(() => {
    writeFavorites([]);
    setFavorites([]);
  }, []);

  const isFavorite = useCallback((videoId: string) => favorites.includes(videoId), [favorites]);

  return { favorites, isFavorite, toggle, remove, clear, count: favorites.length };
}